import { useEffect, useMemo, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Search, Check, X, Trash2, Plus, ChevronLeft, ChevronRight, Tag } from "lucide-react";
import { CATEGORY_LABELS, CATEGORY_ORDER, CATEGORY_UNITS } from "../lib/categories";

const PAGE_SIZE = 25;

export default function Catalog() {
  const [items, setItems] = useState([]);
  const [category, setCategory] = useState("all");
  const [query, setQuery] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [newItem, setNewItem] = useState({ name: "", category: "macaron", unit: "pcs", price: 0, low_stock_threshold: 50, current_stock: 0 });
  const [err, setErr] = useState("");
  const [page, setPage] = useState(0);
  // Dynamic categories (built-in + admin-created) loaded from backend.
  const [categories, setCategories] = useState(
    CATEGORY_ORDER.map((k) => ({ key: k, label: CATEGORY_LABELS[k], built_in: true }))
  );
  const [catManagerOpen, setCatManagerOpen] = useState(false);
  const [newCatLabel, setNewCatLabel] = useState("");
  const [catErr, setCatErr] = useState("");
  // Inline "+ New" flow inside the Add Item modal
  const [inlineNewCat, setInlineNewCat] = useState("");

  const loadCategories = () =>
    api.get("/categories").then((r) => {
      setCategories(r.data.map((c) => ({ key: c.key, label: c.label, id: c.id, built_in: c.built_in })));
    }).catch(() => {});

  const refresh = () => api.get("/inventory").then((r) => setItems(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { refresh(); loadCategories(); }, []);

  const startEdit = (i) => {
    setEditingId(i.id);
    setEdit({ price: i.price, low_stock_threshold: i.low_stock_threshold });
  };
  const saveEdit = async (id) => {
    await api.patch(`/inventory/${id}`, {
      price: parseFloat(edit.price),
      low_stock_threshold: parseInt(edit.low_stock_threshold, 10),
    });
    toast.success("Catalog updated");
    setEditingId(null);
    refresh();
  };
  const toggleAvailable = async (i) => {
    await api.patch(`/inventory/${i.id}`, { available: !i.available });
    toast.success(`${i.name} — ${!i.available ? "shown" : "hidden"} in the order form`);
    refresh();
  };
  const removeItem = async (i) => {
    if (!window.confirm(`Delete ${i.name} from catalog?`)) return;
    await api.delete(`/inventory/${i.id}`);
    toast.success(`Removed "${i.name}"`);
    refresh();
  };
  const addItem = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      let categoryKey = newItem.category;
      if (categoryKey === "__new__") {
        const label = (inlineNewCat || "").trim();
        if (!label) { setErr("Enter a name for the new category"); return; }
        const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
        try {
          await api.post("/categories", { key, label });
        } catch (catErr2) {
          if (catErr2.response?.status !== 400) {
            setErr(formatApiError(catErr2.response?.data?.detail) || catErr2.message);
            return;
          }
        }
        categoryKey = key;
        await loadCategories();
      }
      await api.post("/inventory", { ...newItem, category: categoryKey });
      setAddOpen(false);
      setInlineNewCat("");
      toast.success(`Added "${newItem.name}" to catalog`);
      setNewItem({ name: "", category: categoryKey, unit: "pcs", price: 0, low_stock_threshold: 50, current_stock: 0 });
      refresh();
    } catch (e2) {
      setErr(formatApiError(e2.response?.data?.detail) || e2.message);
    }
  };

  const createCategory = async (e) => {
    e.preventDefault();
    setCatErr("");
    const label = (newCatLabel || "").trim();
    if (!label) { setCatErr("Enter a category name"); return; }
    const key = label.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    try {
      await api.post("/categories", { key, label });
      toast.success(`Added category "${label}"`);
      setNewCatLabel("");
      await loadCategories();
    } catch (e) {
      setCatErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const removeCategory = async (cat) => {
    if (!cat?.id) return;
    const noun = cat.built_in ? "built-in" : "custom";
    if (!window.confirm(`Remove ${noun} category "${cat.label}"? (Any items in it must be removed first.)`)) return;
    try {
      await api.delete(`/categories/${cat.id}`);
      toast.success(`Removed "${cat.label}"`);
      await loadCategories();
    } catch (e) {
      toast.error("Couldn't remove category", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  const renameCategory = async (cat, nextLabel) => {
    const label = (nextLabel || "").trim();
    if (!label || label === cat.label) return;
    try {
      await api.patch(`/categories/${cat.id}`, { label });
      toast.success(`Renamed to "${label}"`);
      await loadCategories();
    } catch (e) {
      toast.error("Couldn't rename category", { description: formatApiError(e.response?.data?.detail) || e.message });
    }
  };

  const labelFor = (key) => categories.find((c) => c.key === key)?.label || CATEGORY_LABELS[key] || key;

  // Item counts per category (respects current search but ignores category
  // filter itself) so the picker doubles as a live census — a count next to
  // "All Categories" makes any missing-item mismatch obvious in one glance.
  const countsByCategory = useMemo(() => {
    const q = query.toLowerCase().trim();
    const base = items.filter((i) => !q || i.name.toLowerCase().includes(q));
    const map = { all: base.length };
    for (const it of base) map[it.category] = (map[it.category] || 0) + 1;
    return map;
  }, [items, query]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    return items.filter((i) =>
      (category === "all" || i.category === category) &&
      (!q || i.name.toLowerCase().includes(q))
    );
  }, [items, category, query]);

  // Reset to first page whenever filter or query changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setPage(0); }, [category, query, items.length]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const paged = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);

  return (
    <Layout>
      <PageHeader
        eyebrow="Master Catalog"
        title="Catalog"
        description="Items, prices, and minimum thresholds. Edits propagate to invoices and alerts. Hidden items still exist — the Hidden pill only removes them from franchisee order forms."
        actions={
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCatManagerOpen(true)}
              data-testid="catalog-manage-categories-btn"
              className="h-11 px-4 rounded-xl border border-border bg-card text-sm font-medium hover:bg-secondary inline-flex items-center gap-2"
            >
              <Tag className="h-4 w-4" /> Add / Edit Category
            </button>
            <button
              onClick={() => setAddOpen(true)}
              data-testid="catalog-add-btn"
              className="h-11 px-5 rounded-xl bg-foreground text-background font-medium text-sm inline-flex items-center gap-2 hover:bg-foreground/90"
            >
              <Plus className="h-4 w-4" /> Add Item
            </button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search…" data-testid="catalog-search"
            className="h-11 pl-9 pr-3 rounded-xl border border-input bg-card text-sm w-64"
          />
        </div>
        <select
          value={category} onChange={(e) => setCategory(e.target.value)}
          data-testid="catalog-category-filter"
          className="h-11 rounded-xl border border-input bg-card px-3 text-sm"
        >
          <option value="all">All Categories · {countsByCategory.all || 0}</option>
          {categories.map((c) => (
            <option key={c.key} value={c.key}>
              {c.label} · {countsByCategory[c.key] || 0}
            </option>
          ))}
        </select>
      </div>

      <div className="luxury-card overflow-hidden">
        <table className="w-full text-sm" data-testid="catalog-table">
          <thead className="bg-secondary/60">
            <tr className="text-left">
              <th className="px-5 py-3 label-eyebrow font-medium">Item</th>
              <th className="px-5 py-3 label-eyebrow font-medium">Category</th>
              <th className="px-5 py-3 label-eyebrow font-medium text-right">On Hand</th>
              <th className="px-5 py-3 label-eyebrow font-medium text-right">Low at</th>
              <th className="px-5 py-3 label-eyebrow font-medium text-right">Price</th>
              <th className="px-5 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {paged.map((i) => {
              const editing = editingId === i.id;
              return (
                <tr key={i.id} className="border-t border-border">
                  <td className="px-5 py-3.5">
                    <div className="flex items-center gap-3">
                      {i.color && <span className="h-3 w-3 rounded-full ring-1 ring-border" style={{ background: i.color }} />}
                      <span className="font-medium">{i.name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3.5 text-muted-foreground">{labelFor(i.category)}</td>
                  <td className="px-5 py-3.5 text-right font-medium">{i.current_stock} {i.unit}</td>
                  <td className="px-5 py-3.5 text-right">
                    {editing ? (
                      <input
                        type="number" min="0" value={edit.low_stock_threshold}
                        onChange={(e) => setEdit({ ...edit, low_stock_threshold: e.target.value })}
                        data-testid={`edit-threshold-${i.id}`}
                        className="h-8 w-20 text-right text-sm rounded-md border border-input bg-card px-2"
                      />
                    ) : (
                      <span className="text-muted-foreground">{i.low_stock_threshold}</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    {editing ? (
                      <input
                        type="number" min="0" step="0.01" value={edit.price}
                        onChange={(e) => setEdit({ ...edit, price: e.target.value })}
                        data-testid={`edit-price-${i.id}`}
                        className="h-8 w-24 text-right text-sm rounded-md border border-input bg-card px-2"
                      />
                    ) : (
                      <span className="font-medium tabular-nums">${(i.price ?? 0).toFixed(2)}</span>
                    )}
                  </td>
                  <td className="px-5 py-3.5 text-right">
                    {editing ? (
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => saveEdit(i.id)}
                          data-testid={`save-edit-${i.id}`}
                          className="h-8 w-8 rounded-md bg-foreground text-background flex items-center justify-center"
                        >
                          <Check className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="h-8 w-8 rounded-md border border-border flex items-center justify-center"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end gap-3">
                        <button
                          onClick={() => toggleAvailable(i)}
                          data-testid={`toggle-available-${i.id}`}
                          title={i.available ? "Hide from order form" : "Show in order form"}
                          className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-full font-medium border ${
                            i.available
                              ? "border-[hsl(var(--accent-sage))]/40 text-[hsl(var(--accent-sage))] hover:bg-[hsl(var(--accent-sage-soft))]"
                              : "border-border text-muted-foreground hover:bg-secondary"
                          }`}
                        >
                          {i.available ? "Available" : "Hidden"}
                        </button>
                        <button onClick={() => startEdit(i)} data-testid={`edit-item-${i.id}`} className="text-sm text-muted-foreground hover:text-foreground">
                          Edit
                        </button>
                        <button onClick={() => removeItem(i)} data-testid={`delete-item-${i.id}`} className="text-muted-foreground hover:text-destructive p-1">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
            {paged.length === 0 && (
              <tr><td colSpan="6" className="px-5 py-12 text-center text-muted-foreground text-sm">No items match.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {filtered.length > 0 && (
        <div className="flex items-center justify-between mt-4 px-1" data-testid="catalog-pagination">
          <div className="text-xs text-muted-foreground">
            Showing <span className="font-medium text-foreground">{safePage * PAGE_SIZE + 1}</span>
            –<span className="font-medium text-foreground">{Math.min((safePage + 1) * PAGE_SIZE, filtered.length)}</span>
            {" "}of <span className="font-medium text-foreground">{filtered.length}</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0}
              data-testid="catalog-prev-page"
              className="h-9 px-3 rounded-lg border border-border bg-card text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 hover:bg-secondary"
            >
              <ChevronLeft className="h-4 w-4" /> Prev
            </button>
            <span className="text-xs text-muted-foreground">
              Page <span className="font-medium text-foreground">{safePage + 1}</span> of {pageCount}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              disabled={safePage >= pageCount - 1}
              data-testid="catalog-next-page"
              className="h-9 px-3 rounded-lg border border-border bg-card text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 hover:bg-secondary"
            >
              Next <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {addOpen && (
        <div className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center px-4" onClick={() => setAddOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-card rounded-2xl border border-border w-full max-w-md p-8" data-testid="catalog-add-modal">
            <h2 className="font-display text-2xl font-light tracking-tight mb-1">New Catalog Item</h2>
            <p className="text-sm text-muted-foreground mb-6">Define what we sell — sets the wholesale price.</p>
            <form onSubmit={addItem} className="space-y-3">
              <div>
                <label className="label-eyebrow mb-2 block">Name</label>
                <input required value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  data-testid="catalog-new-name"
                  className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="label-eyebrow mb-2 block">Category</label>
                  <select value={newItem.category} onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    data-testid="catalog-new-category"
                    className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm">
                    {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                    <option value="__new__">＋ Create new category…</option>
                  </select>
                </div>
                <div>
                  <label className="label-eyebrow mb-2 block">Unit</label>
                  <input value={newItem.unit} onChange={(e) => setNewItem({ ...newItem, unit: e.target.value })}
                    className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm" />
                </div>
              </div>
              {newItem.category === "__new__" && (
                <div>
                  <label className="label-eyebrow mb-2 block">New category name</label>
                  <input
                    autoFocus value={inlineNewCat}
                    onChange={(e) => setInlineNewCat(e.target.value)}
                    placeholder="e.g. Coffee Beans, Merchandise"
                    data-testid="catalog-inline-new-category"
                    className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm"
                  />
                </div>
              )}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="label-eyebrow mb-2 block">Price</label>
                  <input type="number" step="0.01" min="0" value={newItem.price}
                    onChange={(e) => setNewItem({ ...newItem, price: parseFloat(e.target.value || "0") })}
                    data-testid="catalog-new-price"
                    className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm" />
                </div>
                <div>
                  <label className="label-eyebrow mb-2 block">Min</label>
                  <input type="number" min="0" value={newItem.low_stock_threshold}
                    onChange={(e) => setNewItem({ ...newItem, low_stock_threshold: parseInt(e.target.value || "0", 10) })}
                    className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm" />
                </div>
                <div>
                  <label className="label-eyebrow mb-2 block">Stock</label>
                  <input type="number" min="0" value={newItem.current_stock}
                    onChange={(e) => setNewItem({ ...newItem, current_stock: parseInt(e.target.value || "0", 10) })}
                    className="h-12 w-full rounded-xl border border-input bg-card px-3 text-sm" />
                </div>
              </div>
              {err && <div className="text-xs text-destructive">{err}</div>}
              <div className="flex gap-2 pt-3">
                <button type="button" onClick={() => setAddOpen(false)} className="h-11 flex-1 rounded-xl border border-border bg-card text-sm font-medium">Cancel</button>
                <button type="submit" data-testid="catalog-submit-new" className="h-11 flex-1 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90">Create</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {catManagerOpen && (
        <div
          className="fixed inset-0 z-50 bg-foreground/30 backdrop-blur-sm flex items-center justify-center px-4"
          onClick={() => { setCatManagerOpen(false); setCatErr(""); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-card rounded-2xl border border-border w-full max-w-md p-8"
            data-testid="catalog-category-manager-modal"
          >
            <h2 className="font-display text-2xl font-light tracking-tight mb-1">Add / Edit Categories</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Rename any category by clicking its name. A category can be deleted once every item in it has been reassigned or removed.
            </p>
            <form onSubmit={createCategory} className="flex gap-2 mb-5">
              <input
                value={newCatLabel}
                onChange={(e) => setNewCatLabel(e.target.value)}
                placeholder="New category name (e.g. Coffee Beans)"
                data-testid="category-manager-new-label"
                className="h-11 flex-1 rounded-xl border border-input bg-card px-3 text-sm"
              />
              <button
                type="submit"
                data-testid="category-manager-add-btn"
                className="h-11 px-5 rounded-xl bg-foreground text-background text-sm font-medium hover:bg-foreground/90 inline-flex items-center gap-2"
              >
                <Plus className="h-4 w-4" /> Add
              </button>
            </form>
            {catErr && <div className="text-xs text-destructive mb-3">{catErr}</div>}
            <div className="space-y-1.5 max-h-72 overflow-auto pr-1" data-testid="category-manager-list">
              {categories.map((c) => (
                <CategoryRow
                  key={c.id || c.key}
                  category={c}
                  onRename={renameCategory}
                  onRemove={removeCategory}
                />
              ))}
            </div>
            <div className="flex justify-end pt-5">
              <button
                onClick={() => { setCatManagerOpen(false); setCatErr(""); }}
                data-testid="category-manager-close-btn"
                className="h-11 px-5 rounded-xl border border-border bg-card text-sm font-medium hover:bg-secondary"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}


/**
 * A single row in the Add / Edit Categories modal. Click the label to rename
 * it inline (any category, built-in included — Feb 2026 admin request:
 * "don't lock anything, let her change what she wants"). Delete removes the
 * category once its items have been reassigned; the backend still enforces
 * "no items in it" as a data-safety check.
 */
function CategoryRow({ category, onRename, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(category.label);
  const commit = () => {
    setEditing(false);
    if (draft.trim() && draft !== category.label) onRename(category, draft);
  };
  return (
    <div className="flex items-center justify-between px-3 py-2 rounded-lg border border-border bg-secondary/30">
      <div className="min-w-0 flex-1 pr-3">
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") commit();
              if (e.key === "Escape") { setDraft(category.label); setEditing(false); }
            }}
            data-testid={`category-manager-rename-${category.key}`}
            className="h-8 w-full text-sm rounded-md border border-input bg-card px-2"
          />
        ) : (
          <button
            type="button"
            onClick={() => { setDraft(category.label); setEditing(true); }}
            data-testid={`category-manager-label-${category.key}`}
            className="text-sm font-medium text-left w-full truncate hover:text-foreground/80"
            title="Click to rename"
          >
            {category.label}
          </button>
        )}
        <div className="text-[11px] text-muted-foreground">
          {category.built_in ? "Built-in" : "Custom"} · key: {category.key}
        </div>
      </div>
      <button
        onClick={() => onRemove(category)}
        data-testid={`category-manager-remove-${category.key}`}
        className="h-8 w-8 rounded-lg border border-border text-muted-foreground hover:text-destructive hover:border-destructive/40 flex items-center justify-center"
        aria-label={`Remove ${category.label}`}
      >
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
